/**
 * Zentrale Definition aller Router-Namen als Konstanten
 * Verhindert Magic Strings und ermöglicht typsichere Navigation
 */
export const ROUTE_NAMES = {
    // Hauptbereiche
    DASHBOARD: 'dashboard',

    // Proben-Routen
    PROBEN: {
        LISTE: 'proben-liste',
        NEU: 'proben-neu',
        EDIT: 'proben-edit',
        MISCHPROBE_BEARBEITEN: 'proben-mischprobe-bearbeiten',
        TESTZEITPUNKT_BEARBEITEN: 'proben-testzeitpunkt-bearbeiten',
        EXCEL_IMPORT: 'proben-excel-import',
        PROBENNUMMERN_ZUORDNEN: 'proben-probennummern-zuordnen',
        TEXTVORLAGEN: 'proben-textvorlagen',
        PRUEFPLAENE: 'proben-pruefplaene',
        NICHT_FREIGEGEBENE_WARNUNG: 'proben-nicht-freigegebene-warnung'
    },

    // Mustervorlagen-Routen (parallel zu Proben)
    MUSTERVORLAGEN: {
        LISTE: 'mustervorlagen-liste',
        NEU: 'mustervorlagen-neu',
        EDIT: 'mustervorlagen-edit',
        MISCHPROBE_BEARBEITEN: 'mustervorlagen-mischprobe-bearbeiten',
        FILTER: 'mustervorlagen-filter',
        TEXTVORLAGEN: 'mustervorlagen-textvorlagen',
        PRUEFPLAENE: 'mustervorlagen-pruefplaene'
    },

    ETIKETTEN: {
        LISTE: 'etikettendruck',
        FILTER: 'etikettendruck-filter'
    },

    PROBENBEGLEITSCHEIN: {
        LISTE: 'probenbegleitschein-druck',
        FILTER: 'probenbegleitschein-druck-filter'
    },

    // Ergebnis-Routen
    ERGEBNISSE: {
        ENDGUELTIGE: 'ergebnisse-endgueltige',
        VORLAEUFIGE: 'ergebnisse-vorlaeufige',
        IM_LABOR: 'ergebnisse-im-labor',
        ANGEMELDETE: 'ergebnisse-angemeldete',
        DETAIL_ENDGUELTIGE: 'ergebnisse-endgueltige-detail',
        DETAIL_VORLAEUFIGE: 'ergebnisse-vorlaeufige-detail',
        DETAIL_IM_LABOR: 'ergebnisse-im-labor-detail',
        DETAIL_ANGEMELDETE: 'ergebnisse-angemeldete-detail',
        FILTER: 'ergebnisse-filter',
        STATISTIK: 'ergebnisse-statistik',
        SCHNELLSUCHE: 'schnellsuche'
    },

    ZERTIFIKATE: {
        QUALITAETSKONTROLLE: 'zertifikate-qualitaetskontrolle'
    },

    BEGEHUNGSBERICHTE: {
        LISTE: 'begehungsberichte-liste',
        DETAIL: 'begehungsberichte-detail'
    },

    GUTACHTEN: {
        ERFASSEN: 'gutachten-erfassen',
        IN_BEARBEITUNG: 'gutachten-in-bearbeitung',
        TYP_AUSWAHL: 'gutachten-typ-auswahl',
        NEU: 'gutachten-neu',
        EDIT: 'gutachten-edit',
        ERGEBNISSE: 'gutachten-ergebnisse',
        ROHSTOFFSPEZIFIKATIONEN: 'gutachten-rohstoffspezifikationen',
        FILTER: 'gutachten-filter',
        DETAIL: 'gutachten-detail',
        DETAIL_IN_BEARBEITUNG: 'gutachten-detail-in-bearbeitung'
    },

    // Einstellungen/Kundenbereich
    KUNDENBEREICH: {
        UEBERSICHT: 'einstellungen',
        TABELLEN_ERGEBNIS: 'einstellungen-ergebnistabellen',
        TABELLEN_ERFASSEN: 'einstellungen-erfassentabellen',
        EXCEL_EXPORT: 'einstellungen-excel-export',
        EXCEL_EXPORT_LAYOUT: 'einstellungen-excel-export-optionen-layout',
        EXCEL_EXPORT_PARAMETER: 'einstellungen-excel-export-optionen-parameter',
        TABELLEN_GUTACHTEN_ERFASSEN: 'einstellungen-gutachten-erfassen',
        TABELLEN_GUTACHTEN_ERGEBNISSE: 'einstellungen-gutachten-versionen',
        EIGENSCHAFTEN: 'einstellungen-eigenschaften',
        PASSWORT: 'einstellungen-passwort',
        NUTZUNGSBEDINGUNGEN: 'nutzungsbedingungen'
    },

    WISSENSDATENBANK: {
        UEBERSICHT: 'wissensdatenbank'
    },

    // UI Kit Routen
    UIKIT: {
        FORM_LAYOUT: 'uikit-formlayout',
        INPUT: 'uikit-input',
        BUTTON: 'uikit-button',
        TABLE: 'uikit-table',
        LIST: 'uikit-list',
        TREE: 'uikit-tree',
        PANEL: 'uikit-panel',
        OVERLAY: 'uikit-overlay',
        MEDIA: 'uikit-media',
        MESSAGE: 'uikit-message',
        FILE: 'uikit-file',
        MENU: 'uikit-menu',
        CHARTS: 'uikit-charts',
        MISC: 'uikit-misc',
        TIMELINE: 'uikit-timeline'
    },

    // Seiten-Routen
    PAGES: {
        EMPTY: 'pages-empty',
        CRUD: 'pages-crud',
        DOCUMENTATION: 'pages-documentation',
        NOT_FOUND: 'pages-notfound'
    },

    // Auth-Routen
    AUTH: {
        LOGIN: 'auth-login',
        BAV_LIMS_ANMELDEN: 'bav-lims-anmelden'
    },

    // Hilfe
    HILFE: 'hilfe',

    // Sonstige Routen
    LANDING: 'landing',
    ZUGRIFF_VERWEIGERT: 'zugriff-verweigert',
    ZUGRIFF_VERWEIGERT_EINGELOGGT: 'zugriff-verweigert-eingeloggt',
    NOT_FOUND: 'not-found',
    FEHLER: 'error',
    FEHLER_EINGELOGGT: 'error-eingeloggt',
    VORTEILE_VON_MYBAV: 'vorteile-von-mybav'
} as const;

// Type für alle verfügbaren Routennamen
export type RouteNameType =
    | (typeof ROUTE_NAMES)[keyof typeof ROUTE_NAMES]
    | (typeof ROUTE_NAMES.PROBEN)[keyof typeof ROUTE_NAMES.PROBEN]
    | (typeof ROUTE_NAMES.MUSTERVORLAGEN)[keyof typeof ROUTE_NAMES.MUSTERVORLAGEN]
    | (typeof ROUTE_NAMES.ETIKETTEN)[keyof typeof ROUTE_NAMES.ETIKETTEN]
    | (typeof ROUTE_NAMES.PROBENBEGLEITSCHEIN)[keyof typeof ROUTE_NAMES.PROBENBEGLEITSCHEIN]
    | (typeof ROUTE_NAMES.ERGEBNISSE)[keyof typeof ROUTE_NAMES.ERGEBNISSE]
    | (typeof ROUTE_NAMES.BEGEHUNGSBERICHTE)[keyof typeof ROUTE_NAMES.BEGEHUNGSBERICHTE]
    | (typeof ROUTE_NAMES.WISSENSDATENBANK)[keyof typeof ROUTE_NAMES.WISSENSDATENBANK]
    | (typeof ROUTE_NAMES.GUTACHTEN)[keyof typeof ROUTE_NAMES.GUTACHTEN]
    | (typeof ROUTE_NAMES.UIKIT)[keyof typeof ROUTE_NAMES.UIKIT]
    | (typeof ROUTE_NAMES.PAGES)[keyof typeof ROUTE_NAMES.PAGES]
    | (typeof ROUTE_NAMES.AUTH)[keyof typeof ROUTE_NAMES.AUTH]
    | (typeof ROUTE_NAMES.ZERTIFIKATE)[keyof typeof ROUTE_NAMES.ZERTIFIKATE];
